import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, CheckCircle2, AlertTriangle, Loader2, Unplug, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Ms365AppCard } from "./Ms365AppCard";
import { SendSwitch } from "./SendSwitch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ c }) => {
  if (!c) return <span className="text-xs font-bold text-gray-500 bg-gray-100 rounded-full px-2.5 py-1" data-testid="outlook-status-badge">Not connected</span>;
  return c.status === "connected"
    ? <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1" data-testid="outlook-status-badge"><CheckCircle2 className="w-3.5 h-3.5" />Connected</span>
    : <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 rounded-full px-2.5 py-1" data-testid="outlook-status-badge"><AlertTriangle className="w-3.5 h-3.5" />Needs sign-in</span>;
};

const TestResults = ({ msgs }) => (
  <div className="rounded-lg border bg-gray-50 p-3 space-y-1.5" data-testid="outlook-test-results">
    <div className="text-xs font-bold text-gray-500">LATEST {msgs.length} IN INBOX</div>
    {msgs.map((m, i) => (
      <div key={i} className="text-sm flex gap-2 min-w-0"><span className="text-gray-400 shrink-0">{new Date(m.received).toLocaleString([], { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
        <span className="truncate"><b>{m.subject || "(no subject)"}</b> <span className="text-gray-500">· {m.from}</span></span></div>
    ))}
  </div>
);

// Settings → Microsoft 365 / Outlook: one-click connect of this location's mailbox (+ super-admin app keys card).
export const OutlookConnect = ({ user }) => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [msgs, setMsgs] = useState(null);
  const load = () => axios.get(`${API}/outlook/status`).then((r) => setS(r.data)).catch(() => setS({}));
  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search);
    if (p.get("outlook") === "connected") toast.success("Outlook connected!");
    if (p.get("outlook") === "error") toast.error(p.get("msg") || "Outlook sign-in failed");
    if (p.get("outlook")) window.history.replaceState({}, "", window.location.pathname);
  }, []);
  const act = async (k, fn) => { setBusy(k); try { await fn(); } catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); } finally { setBusy(""); } };
  const connect = () => act("connect", async () => { window.location.assign((await axios.get(`${API}/outlook/connect`)).data.authorize_url); });
  const test = () => act("test", async () => { setMsgs((await axios.post(`${API}/outlook/test`)).data.messages); toast.success("Mailbox read OK"); });
  const disconnect = () => act("disc", async () => { if (!window.confirm("Disconnect this location's Outlook mailbox?")) return; await axios.delete(`${API}/outlook/connection`); setMsgs(null); load(); });
  const c = s?.connection;
  return (
    <>
      {user?.role === "super_admin" && <Ms365AppCard onSaved={load} />}
      <Card className="card" data-testid="outlook-connect-card">
        <CardHeader>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="p-2 bg-blue-100 rounded-lg"><Mail className="h-6 w-6 text-blue-600" /></div>
            <div className="flex-1 min-w-[220px]">
              <CardTitle className="text-2xl" style={{ fontFamily: "Manrope" }}>Outlook Mailbox</CardTitle>
              <CardDescription className="mt-1">Connect this location's Microsoft 365 mailbox so FourPawIQ can read new leads and send follow-ups. One sign-in, stays connected.</CardDescription>
            </div>
            <StatusBadge c={c} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!s ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : !s.app_configured ? (
            <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3" data-testid="outlook-app-missing">The Microsoft 365 app isn't set up yet. A super admin needs to add it once (Settings → Microsoft 365 app).</p>
          ) : (
            <>
              {c && <div className="text-sm text-gray-700" data-testid="outlook-mailbox">Mailbox: <b>{c.email}</b>{c.display_name ? ` (${c.display_name})` : ""}
                {c.status !== "connected" && c.last_error && <div className="text-xs text-amber-700 mt-1">Microsoft said: {c.last_error}</div>}</div>}
              {s.can_manage ? (
                <div className="flex flex-wrap gap-2">
                  {(!c || c.status !== "connected") && <Button onClick={connect} disabled={!!busy} className="bg-blue-600 hover:bg-blue-700" data-testid="outlook-connect-btn">
                    {busy === "connect" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}{c ? "Reconnect Outlook" : "Connect Outlook"}</Button>}
                  {c?.status === "connected" && <Button variant="outline" onClick={test} disabled={!!busy} data-testid="outlook-test-btn">
                    {busy === "test" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Inbox className="w-4 h-4 mr-2" />}Test connection</Button>}
                  {c && <Button variant="ghost" onClick={disconnect} disabled={!!busy} className="text-rose-600" data-testid="outlook-disconnect-btn"><Unplug className="w-4 h-4 mr-2" />Disconnect</Button>}
                </div>
              ) : <p className="text-xs text-gray-500">Only a super admin or this location's admin can connect the mailbox.</p>}
              {msgs && <TestResults msgs={msgs} />}
              {c?.status === "connected" && <SendSwitch c={c} canManage={s.can_manage} onChange={load} />}
              {!c && <p className="text-xs text-gray-500">Sign in with the location's own mailbox (e.g. gardencity@k9resorts.com), not a personal one.</p>}
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
};
